import { useLocation } from "@tanstack/react-router";
import { DashboardHeader } from "./AppHeader/DashboardHeader";
import { OffersHeader } from "./AppHeader/OffersHeader";
import { SimpleHeader } from "./AppHeader/SimpleHeader";
import { MapHeader } from "./AppHeader/MapHeader";

interface AppHeaderProps {
  user: any; // User type from Convex
  currentLang: "ro" | "en";
  // Offers header props
  offersSearchQuery?: string;
  onOffersSearchChange?: (query: string) => void;
  offersViewMode?: "grid" | "list";
  onOffersViewModeChange?: (mode: "grid" | "list") => void;
  offersSelectedCategory?: string | null;
  onOffersCategoryChange?: (category: string | null) => void;
  offersSortBy?: "discount" | "distance" | "popularity";
  onOffersSortChange?: (sort: "discount" | "distance" | "popularity") => void;
  offersShowFilters?: boolean;
  onOffersFiltersToggle?: () => void;
  offersCategories?: any[];
  // Map header props
  mapTheme?: string;
  onMapThemeChange?: (theme: string) => void;
  mapThemes?: Array<{ id: string; name: string; icon: string }>;
  // Offer detail header props
  offerDetailIsFavorite?: boolean;
  onOfferDetailToggleFavorite?: () => void;
  // Invites header props
  invitesStats?: {
    available: number;
    used: number;
    activated: number;
  };
}

export function AppHeader(props: AppHeaderProps) {
  const location = useLocation();
  const currentPath = location.pathname;

  // Don't show header on offer detail page
  if (currentPath.match(/^\/app\/offers\/[^/]+$/)) {
    return null;
  }

  // Determine which header to show based on route
  let headerComponent = null;

  if (currentPath === "/app" || currentPath === "/app/") {
    headerComponent = (
      <DashboardHeader
        user={props.user}
        currentLang={props.currentLang}
      />
    );
  } else if (currentPath === "/app/offers" || currentPath.startsWith("/app/offers/")) {
    if (props.onOffersSearchChange && props.onOffersViewModeChange && props.onOffersCategoryChange && props.onOffersSortChange && props.onOffersFiltersToggle) {
      headerComponent = (
        <OffersHeader
          user={props.user}
          currentLang={props.currentLang}
          searchQuery={props.offersSearchQuery || ""}
          onSearchChange={props.onOffersSearchChange!}
          viewMode={props.offersViewMode || "list"}
          onViewModeChange={props.onOffersViewModeChange!}
          selectedCategory={props.offersSelectedCategory ?? null}
          onCategoryChange={props.onOffersCategoryChange!}
          sortBy={props.offersSortBy || "discount"}
          onSortChange={props.onOffersSortChange!}
          showFilters={props.offersShowFilters ?? false}
          onFiltersToggle={props.onOffersFiltersToggle!}
          categories={props.offersCategories}
        />
      );
    }
  } else if (currentPath === "/app/map") {
    if (props.onMapThemeChange) {
      headerComponent = (
        <MapHeader
          user={props.user}
          currentLang={props.currentLang}
          theme={props.mapTheme || "default"}
          onThemeChange={props.onMapThemeChange!}
          themes={props.mapThemes || []}
        />
      );
    }
  } else if (currentPath === "/app/favorites") {
    headerComponent = (
      <SimpleHeader
        user={props.user}
        currentLang={props.currentLang}
        title={props.currentLang === "ro" ? "Favorite" : "Favorites"}
      />
    );
  } else if (currentPath === "/app/history") {
    headerComponent = (
      <SimpleHeader
        user={props.user}
        currentLang={props.currentLang}
        title={props.currentLang === "ro" ? "Istoric" : "History"}
      />
    );
  } else if (currentPath === "/app/profile") {
    headerComponent = (
      <SimpleHeader
        user={props.user}
        currentLang={props.currentLang}
        title={props.currentLang === "ro" ? "Profilul meu" : "My Profile"}
      />
    );
  } else if (currentPath === "/app/invites") {
    headerComponent = (
      <SimpleHeader
        user={props.user}
        currentLang={props.currentLang}
        title={props.currentLang === "ro" ? "Invitații" : "Invites"}
        stats={props.invitesStats}
      />
    );
  }

  if (!headerComponent) {
    return null;
  }

  return (
    <>
      {/* Header fix */}
      <header className="fixed top-0 left-0 right-0 bg-white text-primary px-4 pt-04 pb-4 rounded-b-3xl safe-area-top-padding flex-shrink-0 z-40 shadow-lg">
        {headerComponent}
      </header>
    </>
  );
}
